import { ReactComponent as LocationIcon } from "assets/images/svgs/locationDark.svg";
import { ReactComponent as CalandarIcon } from "assets/images/svgs/calendarDark.svg";
import { ReactComponent as ClockIcon } from "assets/images/svgs/clockdark.svg";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import experienceDetailBG from "assets/images/others/host-side-images/experienceDetailBG.png";
import { ROLES } from "utils/helpers/enums/roles.enum";
import useStore from "hooks/useStore";
import useAttendee from "containers/attendee/useHooks";
import { BookmarkFilled, BookmarkUnfilled } from "assets/images/svgs";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import useUtils from "hooks/useUtils";

interface ExperienceDetailHeaderProps {
  event: EventDTO;
  breadcrumb?: React.ReactNode;
  onHostClick?: () => void;
  onSeeMoreSessions?: () => void;
  onAttendeesClick?: () => void;
  visibleSessions?: any[];
  hasMoreSessions?: boolean;
  backgroundImage?: string;
  gradientClass?: string;
}

const ExperienceDetailHeader: React.FC<ExperienceDetailHeaderProps> = ({
  event,
  breadcrumb,
  onHostClick,
  onSeeMoreSessions,
  onAttendeesClick,
  visibleSessions = [],
  hasMoreSessions = false,
  backgroundImage = experienceDetailBG,
  gradientClass = "host-bg-gradient",
}) => {
  const attendingCount = event?.eventSubscriptions?.length || 0;
  const { addToWishlist, removeFromWishlist } = useAttendee();
  const { getCurrentRole } = useStore();
  const { formatDateToMMDDYYYY } = useUtils();
  const userRole = getCurrentRole();

  // Function to open Google Maps with the event location
  const openGoogleMaps = () => {
    if (event?.location) {
      const encodedLocation = encodeURIComponent(event.location);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Sort sessions by startDateTime (earliest first)
  const sortedVisibleSessions = [...visibleSessions].sort((a, b) => {
    const dateA = new Date(a.startDateTime || a.startDateWithMonthAndYear);
    const dateB = new Date(b.startDateTime || b.startDateWithMonthAndYear);
    return dateA.getTime() - dateB.getTime();
  });

  // const handleToggleWishlist = () => {
  //   event.isWishlisted = !event.isWishlisted;
  //   if (event.isWishlisted) {
  //     addToWishlist(event.id);
  //   } else {
  //     removeFromWishlist(event.id);
  //   }
  // };

  const handleToggleWishlist = async () => {
    const result = await confirmationPopup(
      `${
        event?.isWishlisted
          ? "Are you sure you want to remove from the wishlist?"
          : "Are you sure you want to add to the wishlist?"
      }`
    );

    if (!result.isConfirmed) return;

    event.isWishlisted = !event.isWishlisted;
    if (event.isWishlisted) {
      addToWishlist(event.id);
    } else {
      removeFromWishlist(event.id);
    }
  };

  const renderAttendingCount = () => {
    if (!attendingCount || attendingCount <= 0) {
      return <span className="text-base text-lightBlackText ml-2">-</span>;
    }

    if (attendingCount > 3) {
      return (
        <span className="text-base text-lightBlackText ml-3">3+ attendees</span>
      );
    }

    return (
      <span className="text-base text-lightBlackText ml-3">
        {attendingCount} {attendingCount === 1 ? "attendee" : "attendees"}
      </span>
    );
  };

  return (
    <div
      style={{ backgroundImage: `url(${backgroundImage})` }}
      className="bg-cover bg-center bg-no-repeat h-full lg:min-h-[320px] w-full"
    >
      <div className={`h-full lg:min-h-[320px] ${gradientClass}`}>
        <div className="container">
          <div className="flex justify-between gap-4 items-center mb-6">
            {/* Breadcrumb */}
            {breadcrumb && (
              <div className="flex flex-wrap justify-between gap-6 items-center">
                {breadcrumb}
              </div>
            )}
            {userRole === ROLES.ATTENDEE && (
              <div
                className=" bg-white text-center p-2 rounded-md cursor-pointer"
                onClick={handleToggleWishlist}
              >
                {event?.isWishlisted ? (
                  <BookmarkFilled title="Remove from Wishlist" />
                ) : (
                  <BookmarkUnfilled title="Add To Wishlist" />
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-6 items-center">
            {/* Left Section: Event Details */}
            <div className="flex items-center">
              <div className="flex flex-col gap-3">
                {/* Location */}
                <p
                  className="flex items-center text-base text-lightBlackText cursor-pointer"
                  onClick={openGoogleMaps}
                  title="Click to open in Google Maps"
                >
                  <LocationIcon className="mr-1" /> {event.location}
                </p>

                {/* Title */}
                <h1 className="text-blackie capitalize text-2xl md:3xl lg:text-5xl w-full md:max-w-xl font-medium">
                  {event.title}
                </h1>

                {/* Sessions */}
                {sortedVisibleSessions.map((session, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-1 text-base text-lightBlackText"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1">
                        <CalandarIcon />
                        <span>
                          {formatDateToMMDDYYYY(
                            session.startDateWithMonthAndYear
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon /> {session?.startTime} to {session?.endTime}
                      </div>
                    </div>
                  </div>
                ))}

                {/* See More Sessions */}
                {hasMoreSessions && onSeeMoreSessions && (
                  <button
                    onClick={onSeeMoreSessions}
                    className="text-blue-500 underline text-sm w-fit"
                  >
                    See More
                  </button>
                )}

                <div className="flex flex-wrap gap-6 items-center">
                  {/* Attending Avatars */}
                  <div
                    className={`flex items-center ${
                      onAttendeesClick ? "cursor-pointer" : attendingCount > 0 ? "" : "cursor-default"
                    }`}
                    onClick={onAttendeesClick}
                  >
                    {attendingCount > 0 &&
                      event?.eventSubscriptions
                        ?.slice(0, 3)
                        .map((avatar: any, index: number) => (
                          <img
                            key={index}
                            src={concatPathWithBackendUrl(
                              avatar?.profilePicUrl?.fileDownloadUri
                            )}
                            alt="Attendee"
                            className="w-7 h-7 -mr-2 rounded-full border-2 border-white"
                          />
                        ))}
                    {renderAttendingCount()}
                  </div>

                  {/* Event Info */}
                  <div className="flex flex-wrap gap-5 items-center">
                    <button
                      className={`rounded-md text-white px-4 py-2 ${
                        userRole === ROLES.RSVP
                          ? "bg-rsvpGreen"
                          : "bg-yellowText"
                      }`}
                    >
                      {event.isSession ? "Session" : "Workshop"}
                    </button>
                    <span className="text-2xl text-blackie font-semibold">
                      {event.entryFee > 0
                        ? `$${event.entryFee.toFixed(2)}`
                        : "Free"}
                    </span>
                    <span className="text-xl text-lightBlackText">
                      {event.availableSlots}{" "}
                      {event.availableSlots <= 1 ? "Slot" : "Slots"} Available
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section: Badge & Host */}
            <div className="">
              <div className="flex flex-wrap gap-4">
                {/* Event Badge */}
                {event?.badgeUrlId && (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg w-[193px] h-[217px] bg-white p-3 shadow-md">
                    <img
                      className="w-[126px] h-[126px]"
                      src={getFilePathWithBackendUrl(
                        (event as any)?.badge?.fileDownloadUri
                      )}
                      alt="Event Badge"
                    />
                    <span className="text-lg font-medium text-blackie">
                      Event Badge
                    </span>
                  </div>
                )}

                {/* Host Card */}
                <div
                  onClick={onHostClick}
                  className="flex flex-col items-center cursor-pointer justify-center gap-2 rounded-lg w-[193px] h-[217px] bg-white p-3 shadow-md"
                >
                  <img
                    className="w-[110px] h-[110px] rounded-full object-cover"
                    src={getFilePathWithBackendUrl(
                      (event as any)?.host?.profilePicUrl?.fileDownloadUri
                    )}
                    alt="Host"
                  />
                  <div className="text-center">
                    <p className="font-medium text-blackie text-lg">Host</p>
                    <p className="text-blackie text-base">
                      {event?.host?.firstName || event?.host?.lastName
                        ? `${event?.host?.firstName || ""} ${
                            event?.host?.lastName || ""
                          }`.trim()
                        : "Name"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceDetailHeader;
